import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArtists, setFilters } from '../redux/slices/artistsSlice';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE } from '../utils/paginationUtils';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import ArtistsDrawer from '../components/ArtistsDrawer';

function Artists() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openCreate, setOpenCreate] = useState(false);
  const [editArtist, setEditArtist] = useState(null);

  const { data: artistsData, error, totalItems, options } = useSelector((state) => state.artists);
  const pageParam = searchParams.get('page');
  const limitParam = searchParams.get('limit');
  const searchParam = searchParams.get('search');

  const updateSearchParams = (patch) => {
    const updated = { ...options, ...patch };
    setSearchParams({
      page: String(updated.page),
      limit: String(updated.limit),
      search: updated.search || '',
    });
  };

  const handleSearch = () => {
    updateSearchParams({ page: DEFAULT_PAGE });
  };

  const handleChangePage = (_, newPage) => {
    updateSearchParams({ page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    updateSearchParams({ limit: parseInt(event.target.value, 10), page: DEFAULT_PAGE });
  };

  const onDrawerClose = () => {
    setOpenCreate(false);
    setEditArtist(null);
  };

  const toggleOpenUpdate = (artist) => {
    setEditArtist(artist);
  };

  const toggleOpenCreate = () => {
    setOpenCreate(true);
  };

  useEffect(() => {
    const filters = {
      page: parseInt(pageParam, 10) || DEFAULT_PAGE,
      limit: parseInt(limitParam, 10) || DEFAULT_ROWS_PER_PAGE,
      search: searchParam || '',
    };

    dispatch(setFilters(filters));
    dispatch(fetchArtists(filters));
  }, [dispatch, pageParam, limitParam, searchParam]);

  useEffect(() => {
    if (error) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [error, t]);

  return (
    <div className="artists">
      <Grid container spacing={4}>
        <Grid item container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h3">{t('Artists')}</Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={toggleOpenCreate}>
              {t('Add artist')}
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, width: '1296px', maxWidth: '100%' }}>
            <Grid container alignItems="center" spacing={2} justifyContent="flex-end">
              <Grid item xs={12} sm={8} md={4}>
                <TextField
                  onChange={(event) => dispatch(setFilters({ search: event.target.value }))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  value={options.search}
                  inputProps={{ autoComplete: 'off' }}
                  label={t('Search')}
                  fullWidth
                  placeholder={t('Search artist')}
                  variant="outlined"
                  type="text"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={handleSearch} edge="end">
                        <SearchOutlined />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {artistsData.map((artist) => (
          <Grid key={artist.id} item>
            <Card sx={{ width: '300px', minHeight: '360px' }}>
              <CardHeader
                sx={{
                  '& .MuiCardHeader-content': {
                    overflow: 'hidden',
                  },
                }}
                titleTypographyProps={{ noWrap: true }}
                title={`${artist.first_name} ${artist.last_name}`}
                action={
                  <IconButton onClick={() => toggleOpenUpdate(artist)}>
                    <EditIcon />
                  </IconButton>
                }
              />
              <Divider />
              {!artist.profile_image_url ? (
                <Skeleton variant="rectangular" height="300px" />
              ) : (
                <CardMedia component="img" image={artist.profile_image_url} />
              )}
            </Card>
          </Grid>
        ))}
        <Grid item xs={12}>
          <TablePagination
            component="div"
            count={totalItems}
            page={options.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={options.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE}
          />
        </Grid>
      </Grid>
      <ArtistsDrawer open={openCreate || !!editArtist} artist={editArtist} onClose={onDrawerClose} />
    </div>
  );
}

export default Artists;
